import { InputWrapper } from "common/components/Wrapper/InputBase";
import {
  extractFixed,
  useInitFixed,
  useMultiSelect,
  useSingleSelect,
} from "common/hooks/useSelect";
import {
  useValidCheckGroup,
  useValidCheckSingle,
} from "common/hooks/validCheck";
import React, { useMemo } from "react";
import styled from "styled-components";

export const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

export const HiddenRadio = styled.input.attrs({ type: "radio" })`
  display: none;
`;

const ButtonListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  & > * {
    max-width: ${({ $maxItemWidth }) => $maxItemWidth ?? "none"};
    max-height: ${({ $maxItemHeight }) => $maxItemHeight ?? "none"};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SingleSelectButtonGroup = ({
  //InputWrapper
  label,
  caption,
  direction,

  //SingleSelectButtonGroup
  name,
  listStyle,
  maxItemWidth,
  maxItemHeight,
  onChange,
  required,
  children,
}) => {
  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name: name,
    rules: {
      required: required || false,
    },
  });
  const value = inputProps.value ?? "";

  const fixedValue = useMemo(() => extractFixed(children)[0] ?? "", [children]);
  const { currentValue, setValue } = useSingleSelect({
    value,
    fixedValue,
    onChange: (v) => {
      onValueChange(v);
      onChange?.(v);
    },
  });

  useInitFixed(setValue, onValueChange, fixedValue);

  return (
    <InputWrapper
      label={label}
      caption={caption}
      error={error}
      rules={{ required }}
      direction={direction}>
      <ButtonListWrapper
        $maxItemWidth={maxItemWidth}
        $maxItemHeight={maxItemHeight}
        style={listStyle}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            selected: child.props.value === currentValue,
            onSelect: () => setValue(child.props.value),
          }),
        )}
      </ButtonListWrapper>
    </InputWrapper>
  );
};

export const MultiSelectButtonGroup = ({
  //InputWrapper
  label,
  caption,
  direction,

  //MultiSelectButtonGroup
  name,
  style,
  maxItemWidth,
  maxItemHeight,
  onChange,
  required,
  disabled,
  children,
}) => {
  const { inputProps, error, onValueChange } = useValidCheckGroup(name, {
    required: required || false,
  });
  const values = inputProps.value ?? [];

  const fixedValues = useMemo(() => extractFixed(children), [children]);
  const { currentSet, toggle, setValue } = useMultiSelect({
    value: values,
    fixedValues,
    onChange: (v) => {
      onValueChange(v);
      onChange?.(v);
    },
  });

  useInitFixed(setValue, onValueChange, fixedValues);

  return (
    <InputWrapper
      label={label}
      caption={caption}
      error={error}
      rules={{ required }}
      direction={direction}>
      <ButtonListWrapper
        $maxItemWidth={maxItemWidth}
        $maxItemHeight={maxItemHeight}
        style={style}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            selected: currentSet.has(child.props.value),
            onSelect: disabled
              ? undefined
              : () => {
                  if (child.props.onSelect) {
                    child.props.onSelect(child.props.value);
                  } else {
                    toggle(child.props.value);
                  }
                },
          }),
        )}
      </ButtonListWrapper>
    </InputWrapper>
  );
};
